<template>
  <Transition name="fade">
    <div v-if="visible" class="backTop" @click="scrollToTop">
      <Transition name="to-up">
        <iconpark-icon
          v-if="!scrolling"
          name="zhiding"
          size="22"
          class="top-icon absolute"
        />
        <div
          v-else
          class="flex flex-col justify-center items-center text-12 absolute"
        >
          <div class="text-purple-6">{{ viewCount }}</div>
          <div class="bg-purple-6 h-[1px] w-[22px]"></div>
          <div>{{ total || domTotalCount }}</div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>
<script lang="ts" setup>
import { debounce, throttle } from 'lodash-es'

interface BackTopProps {
  scrollContainer: string | (() => HTMLElement) // 滚动容器
  item: string // 子元素
  top?: number // 滚动到顶部位置
  visibilityHeight?: number
  total?: number
}

const props = withDefaults(defineProps<BackTopProps>(), {
  top: 0,
  visibilityHeight: 400,
})

const visible = ref(false)
const scrolling = ref(false)
const viewCount = ref(0)
const domTotalCount = ref(0)

const getContainer = () => {
  if (typeof props.scrollContainer === 'string') {
    return document.querySelector(props.scrollContainer) as HTMLElement
  }
  return props.scrollContainer()
}

const calVisible = throttle(() => {
  const { visibilityHeight } = props
  const container = getContainer()
  if (container) {
    const scrollTop = container.scrollTop
    visible.value = scrollTop >= visibilityHeight
  }
}, 200)

const calStopScroll = debounce(() => {
  scrolling.value = false
}, 800)

const calCount = throttle(() => {
  const container = getContainer()
  const allItems = container?.querySelectorAll(props.item)
  const containerBottom = container?.getBoundingClientRect().bottom
  domTotalCount.value = allItems?.length
  let visibleCount = 0
  allItems?.forEach((item) => {
    if (item.getBoundingClientRect().bottom < containerBottom) {
      visibleCount++
    }
  })
  viewCount.value = visibleCount
}, 100)

const handleScroll = () => {
  calVisible()
  scrolling.value = true
  calStopScroll()
  calCount()
}

const scrollRemove = () => {
  const container = getContainer()
  calVisible.cancel()
  calStopScroll.cancel()
  container?.removeEventListener('scroll', handleScroll)
}

const bindScrollEvent = () => {
  const container = getContainer()
  container?.addEventListener('scroll', handleScroll)
  handleScroll()
}

const scrollToTop = () => {
  const container = getContainer()
  container?.scrollTo({
    top: props.top,
    behavior: 'smooth',
  })
}

watch(
  () => getContainer(),
  () => {
    scrollRemove()
    nextTick(() => {
      bindScrollEvent()
    })
  }
)

onMounted(() => {
  nextTick(() => {
    bindScrollEvent()
  })
})

onActivated(() => {
  nextTick(() => {
    bindScrollEvent()
  })
})

onDeactivated(() => {
  scrollRemove()
})

onBeforeUnmount(() => {
  scrollRemove()
})
</script>
<style lang="scss" scoped>
.backTop {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: var(--back-1);
  color: var(--text-1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 2px 19px 1px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.to-up-enter-active,
.to-up-leave-active {
  transition: transform 0.25s ease-out;
}

.to-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.to-up-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style>
